import {
  Application,
  BLEND_MODES,
  Circle,
  Container,
  Graphics,
  RenderTexture,
  Sprite,
  filters,
  Point,
  Rectangle,
} from 'pixi.js';

import pixiFilters from 'pixi-filters';
import {TweenLite} from 'gsap';

const app = new Application({
  antialias: false,
  width: window.innerWidth,
  height: window.innerHeight,
});
const backgroundColor = 0x333333
app.renderer.backgroundColor = backgroundColor;

class Planet extends Sprite {
  constructor(radius=100) {
    const graphics = new Graphics();
    graphics.beginFill(0xFFFFFF);
    graphics.drawCircle(radius, radius, radius);
    const texture = RenderTexture.create(radius*2, radius*2);
    app.renderer.render(graphics, texture);
    super(texture);
    this.anchor.x = 0.5;
    this.anchor.y = 0.5;
    this.radius = radius;
    this.updatePixelAlphas();
    this.updateCenterOfMass();
    this.tanks = new Set();
  }

  addTank(tank) {
    this.tanks.add(tank);
    this.addChild(tank);
  }

  updatePixelAlphas() {
    const pixels = app.renderer.extract.pixels(this.texture);
    const pixelAlphas = this.pixelAlphas || new Uint8Array((this.radius*2)**2);
    for (let i = 3; i < pixels.length; i += 4) {
      pixelAlphas[~~(i/4)] = pixels[i];
    }
    this.pixelAlphas = pixelAlphas;
  }

  destroy(...circles) {
    const mask = new Graphics();
    mask.beginFill(0xFFFFFF);
    mask.drawRect(0, 0, this.radius*2, this.radius*2);
    mask.beginFill(0x000000);
    for (const c of circles) {
      mask.drawCircle(c[0] + this.radius, c[1] + this.radius, c[2]);
    }

    const sp = new Sprite(this.texture);
    sp.mask = mask;

    const texture = RenderTexture.create(this.radius*2, this.radius*2);
    app.renderer.render(sp, texture);
    this.texture = texture;
    this.updatePixelAlphas();
    this.updateCenterOfMass();
  }

  updateCenterOfMass() {
    let filledPixels = 0;
    let xSum = 0;
    let ySum = 0;
    const pixelAlphas = this.pixelAlphas;
    const width = this.radius * 2;
    for (let i = 0; i < this.pixelAlphas.length; i++) {
      if (this.pixelAlphas[i] !== 0) {
        filledPixels++;
        xSum += i % width;
        ySum += ~~(i / width);
      }
    }
    this.centerOfMass = new Point(
      xSum/filledPixels - this.radius,
      ySum/filledPixels - this.radius,
    );
    this.mass = filledPixels;
  }

  readAlpha(x, y) {
    x = ~~(x + this.radius);
    y = ~~(y + this.radius);
    // out of bounds
    if (!this.withinTextureBounds(x, y)) {
      return 0;
    }
    return this.pixelAlphas[x + y * this.radius*2];
  }

  withinTextureBounds(x, y) {
    return !(x < 0 || y < 0 || x >= this.radius*2 || y >= this.radius*2);
  }

  computeNormal(x, y, guess, radius=10, steps=20) {
    let hadCollision = null;
    const a = -this.findAlphaTransition(
      x, y, guess - Math.PI/2, Math.PI, radius, steps
    );
    const b = -this.findAlphaTransition(
      x, y, guess + Math.PI/2, Math.PI, radius, steps
    );
    if (a === null || b === null) {
      return guess;
    }
    const avgAngle = (a + b) % (Math.PI*2) / 2;

    // see if there's a planet intersection above or below avgAngle, and try to
    // return what won't cause a planet intersection.
    const avgAngleAlpha = this.readAlpha(
      x + Math.cos(avgAngle) * radius,
      y + Math.sin(avgAngle) * radius,
    );
    const altAvgAngleAlpha = this.readAlpha(
      x + Math.cos(avgAngle + Math.PI) * radius,
      y + Math.sin(avgAngle + Math.PI) * radius,
    );
    if (altAvgAngleAlpha && !avgAngleAlpha) {
      return avgAngle;
    }
    if (avgAngleAlpha && !altAvgAngleAlpha) {
      return avgAngle + Math.PI;
    }

    // That failed. Instead, just take whichever is closest to the original
    // guess we had.
    const fad = findAngleDifference;
    return fad(guess, avgAngle) < fad(guess, avgAngle + Math.PI) ?
      avgAngle :
      avgAngle + Math.PI;
  }

  findAlphaTransition(x, y, startAngle, range, radius, steps) {
    const hadCollision = !!this.readAlpha(
      x + radius * Math.cos(startAngle),
      y - radius * Math.sin(startAngle),
    );
    for (let s = 1; s <= steps; s++) {
      const a = startAngle + s/steps * range/2;
      const b = startAngle - s/steps * range/2;
      if (
        !!this.readAlpha(
          x + radius*Math.cos(a),
          y - radius*Math.sin(a),
        ) !== hadCollision
      ) {
        return a;
      }
      if (
        !!this.readAlpha(
          x + radius*Math.cos(b),
          y - radius*Math.sin(b),
        ) !== hadCollision
      ) {
        return b;
      }
    }
    return null;
  }

  step(dt) {
    for (const tank of this.tanks) {
      tank.step(dt);
    }
  }
}

class Projectile extends Graphics {
  constructor() {
    super();
    this.vx = 0;
    this.vy = 0;
    this.draw();
  }

  draw() {
    this.beginFill(0xFFFFFF);
    this.drawCircle(0, 0, 5);
  }
}

class Tank extends Container {
  constructor(planet) {
    super();

    this.planet = planet;

    const baseGraphics = new Graphics();
    baseGraphics.beginFill(0xFFFFFF);
    baseGraphics.lineStyle(2, backgroundColor);
    //baseGraphics.beginStroke(0x000000);
    baseGraphics.drawPolygon([
      new Point(-15, -9),
      new Point(15, -9),
      new Point(10, 0),
      new Point(-10, 0),
      new Point(-15, -9),
    ]);
    this.baseGraphics = baseGraphics;
    this.addChild(baseGraphics);
  }

  step(dt) {
    this.snapToPlanet();
  }

  snapToPlanet() {
    // already snapped
    if (this.planet.readAlpha(this.x, this.y) !== 0) {
      return;
    }

    const dx = this.x - this.planet.centerOfMass.x;
    const dy = this.y - this.planet.centerOfMass.y;
    const angle = Math.atan2(dy, dx);
    this.rotation = angle + Math.PI/2;

    let withinTextureBounds = this.planet.withinTextureBounds(this.x, this.y);

    const stepDx = dx/Math.max(Math.abs(dx), Math.abs(dy));
    const stepDy = dy/Math.max(Math.abs(dx), Math.abs(dy));
    let stepX = this.x;
    let stepY = this.y;
    // limit iterations in case something goes horribly awry
    for (let i = 0; i < 1000; i++) {
      if (this.planet.readAlpha(stepX, stepY)) {
        this.x = stepX;
        this.y = stepY;
        this.rotation = this.planet.computeNormal(stepX, stepY, angle) + Math.PI/2;
        return;
      }
      stepX -= stepDx;
      stepY -= stepDy;
      const newWithinTextureBounds =
        this.planet.withinTextureBounds(stepX, stepY);
      if (withinTextureBounds && !newWithinTextureBounds) {
        // if we used to be within the texture bounds and left, just give up
        return;
      }
      withinTextureBounds = newWithinTextureBounds;
    }
  }
}

class Universe extends Container {
  constructor() {
    super();
    this.planets = new Set();
    this.projectiles = new Set();
  }

  addPlanet(planet) {
    this.addChild(planet);
    this.planets.add(planet);
  }

  addProjectile(projectile) {
    this.addChild(projectile);
    this.projectiles.add(projectile);
  }

  removeProjectile(projectile) {
    this.removeChild(projectile);
    this.projectiles.delete(projectile);
  }

  step(dt) {
    // update velocity
    for (let step = 0; step < 5; step++) {
      for (const proj of this.projectiles) {
        for (const plt of this.planets) {
          const pltPos = this.toLocal(plt.toGlobal(plt.centerOfMass));
          const dist = Math.sqrt((proj.x - pltPos.x)**2 + (proj.y - pltPos.y)**2);
          const angle = Math.atan2(proj.y - pltPos.y, proj.x - pltPos.x);
          proj.vx += plt.mass / 10000 * (dt * 0.08 * Math.cos(angle) / Math.max(dist, 10)**2);
          proj.vy += plt.mass / 10000 * (dt * 0.08 * Math.sin(angle) / Math.max(dist, 10)**2);
        }

        let collided = false;
        for (let collisionStep = 0; collisionStep < 5; collisionStep++) {
          if (collided) {
            break;
          }
          // update position
          proj.x -= proj.vx * dt * 5/25;
          proj.y -= proj.vy * dt * 5/25;

          // compute collision
          for (const plt of this.planets) {
            const planetPoint = plt.toLocal(
              this.toGlobal(new Point(proj.x, proj.y))
            );
            if (plt.readAlpha(planetPoint.x, planetPoint.y)) {
              this.removeProjectile(proj);
              onCollision(plt.toGlobal(planetPoint))
              plt.destroy([planetPoint.x, planetPoint.y, Math.random() * 50 + 20]);
              collided = true;
              break;
            }
          }
        }
      }
    }
    for (const plt of this.planets) {
      plt.step();
    }
  }
}

const universe = new Universe();
app.stage.addChild(universe);
  /*
const planet = new Planet(~~(Math.min(app.screen.width, app.screen.height) * .3));
planet.x = ~~(app.screen.width/2);
planet.y = ~~(app.screen.height/2);
universe.addPlanet(planet);
*/

const planets = [];
while (true) {
  let success = false;
  for (let i = 0; i < 30; i++) {
    const radius = 100 + Math.random()*200;
    const planet = new Planet(~~radius);
    if (planets.length) {
      planet.x = ~~(50 + Math.random() * (app.screen.width - 100));
      planet.y = ~~(50 + Math.random() * (app.screen.height - 100));
    } else {
      planet.x = ~~(app.screen.width / 2);
      planet.y = ~~(app.screen.height / 2);
    }
    success = true;
    for (const other of planets) {
      if (Math.sqrt((planet.x - other.x)**2 + (planet.y - other.y)**2) < planet.radius+other.radius+100) {
        success = false;
        break;
      }
    }
    if (success) {
      planets.push(planet);
      universe.addPlanet(planet);
      break;
    }
  }
  if (!success) {
    break;
  }
}

//app.stage.filters = [];
//app.stage.filterArea = app.screen;
function onCollision(pt) {
    /*
  const f = new pixiFilters.ShockwaveFilter();
  console.log(f.center);
  pt = app.stage.toLocal(pt)
  console.log(pt);
  f.center = [
    pt.x / app.screen.width,
    pt.y / app.screen.height,
  ];
  f.params = [10, 0.3, 0.01];
  console.log(f.params);

  f.time = 0;
  TweenLite.to(f, 10, {time: 1, params: [10, 0.3, 0], onComplete: () => {
    app.stage.filters = app.stage.filters.slice(1);
  }});
  app.stage.filters = app.stage.filters.concat([f]);
  console.log(app.stage.filters);
  */
}

app.stage.interactive = true;
app.stage.hitArea = app.screen;

let projectile;
let projectileLine;

app.stage.on('mousemove', ev => {
  const pos = universe.toLocal(ev.data.global);
  if (projectileLine != null) {
    universe.removeChild(projectileLine);
  }
  if (projectile != null) {
    projectileLine = new Graphics();
    projectileLine.lineStyle(2, 0xFFFFFF);
    projectileLine.moveTo(projectile.x, projectile.y);
    projectileLine.lineTo(pos.x, pos.y);
    universe.addChild(projectileLine);
  }
  /*tank.x = pos.x;
  tank.y = pos.y;
  tank.snapToPlanet();*/
});

app.stage.on('mousedown', ev => {
  if (projectile) {
    universe.removeChild(projectile);
  }
  projectile = new Projectile();
  const pos = universe.toLocal(ev.data.global);
  projectile.x = pos.x;
  projectile.y = pos.y;
  universe.addChild(projectile);
});

app.stage.on('mouseup', ev => {
  if (projectile != null) {
    universe.removeChild(projectile);
    const pos = universe.toLocal(ev.data.global);
    projectile.vx = (pos.x - projectile.x)*.001;
    projectile.vy = (pos.y - projectile.y)*.001;
    universe.addProjectile(projectile);
    projectile = null;
  }
  if (projectileLine != null) {
    universe.removeChild(projectileLine);
  }
});

/**
 * Normalizes a given angle to between -Math.PI and Math.PI.
 */
function normalizeAngle(a) {
  return (a % (Math.PI*2) + Math.PI) % (Math.PI*2) - Math.PI;
}

function findAngleDifference(a, b) {
  a = normalizeAngle(a);
  b = normalizeAngle(b);
  return Math.min(Math.abs(a-b), Math.abs(b-a));
}

function physicsLoop() {
  // update physics at 60 fps
  universe.step(16.67)
}

function renderLoop() {
  app.render();
  window.requestAnimationFrame(renderLoop);
}

window.addEventListener('load', () => {
  document.body.appendChild(app.view);
  app.ticker.start();
  renderLoop();
  setInterval(physicsLoop, 16.67);
});



// WEBPACK FOOTER //
// ./src/index.js